import type { AutocompletedVehicle } from '#modules/AssetOverview/Tinglysning.types'

import { FlowPage, H3, PrimaryButton, SecondaryButton, TertiaryButton } from '@monthio/design-system'
import {
	BoxInput,
	COLORS,
	makeStyles,
	Select,
	useIntlConfig,
} from '@monthio/react-components'
import { Column } from '#modules/DebtOverview/Components/column'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import type {
	Asset,
	OverarchingAssetType,
	RealEstate,
	RealEstateUnion,
	Vehicle,
	VehicleUnion,
	YearlyMileageType,
} from '../../AssetOverview.types'
import type { Setter } from './FieldModels'

import { Conditionally } from '../../../../modulesTemp/shared/conditionally'
import {
	AssetType,
	overarchingTypes,
	RealEstateFinancingType,
	translateAssetType,
	translateFinancingType,
	translateYearlyMileage,
	validateSpecificFields,
	VehicleFinancingType,
	YearlyMileage,
} from '../../AssetOverview.types'
import { AssetLogo } from '../assetLogo'
import { ButtonSelect } from '../ButtonSelect'
import { useSteps } from '../useSteps'
import {
	AutoCompleteAddress,
	AutoCompleteVehicle,
	toCased,
} from './AutoComplete'
import { AssetTypeSpecific, Background } from './FieldModels'

const AssetSelectorGrid = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 20px;
`

const AssetTypeSelectButton = styled(SecondaryButton)`
	flex-direction: column;

	&:not(:hover) {
		border-color: transparent;
		background: #fff;
	}

	> svg,
	> img {
		width: 50px;
	}
`

export const SelectPlaceholderLabel = styled.div<{ isSelected: boolean }>`
	color: ${(props) =>
		props.isSelected ? COLORS.PRIMARY : COLORS.GREY_SCALE.LIGHT_GREY_DARKER};
`

const SelectAssetSubType: React.FC<{
	overarchingType: OverarchingAssetType
	onChange: (assetType: AssetType) => void
}> = ({ onChange, overarchingType }) => {
	const assetTypes = (
		overarchingTypes as unknown as Record<
			'realEstate' | 'vehicle' | 'securities',
			AssetType[]
		>
	)[overarchingType]

	return (
		<AssetSelectorGrid>
			{assetTypes.map((assetType) => {
				return (
					<AssetTypeSelectButton
						type="button"
						key={assetType}
						onClick={() => onChange(assetType)}
					>
						<AssetLogo assetType={assetType} />
						{translateAssetType(assetType)}
					</AssetTypeSelectButton>
				)
			})}
		</AssetSelectorGrid>
	)
}

export const SelectFinancingType = <T extends Partial<Vehicle | RealEstate>>({
	asset,
	onChange,
}: {
	asset: T
	onChange: (financingType: T['financingType']) => void
}) => {
	const { t } = useTranslation()

	const financingTypes = overarchingTypes.realEstate.includes(
		asset.assetType as RealEstateUnion,
	)
		? RealEstateFinancingType
		: VehicleFinancingType

	return (
		<>
			<H3>{t('asset-overview.add-asset.select-type.header')}</H3>
			<ButtonSelect
				currentValue={asset.financingType}
				values={Object.values(financingTypes).map((value) => ({
					value,
					displayValue: translateFinancingType(value, asset.assetType),
				}))}
				onChange={onChange}
			/>
		</>
	)
}

export const useStyles = makeStyles(() => ({
	root: {
		'&': {
			fontSize: '14px',
		},
		'& .MuiListItem-root ': {
			display: 'block',
			fontSize: '14px',
		},
		'& .MuiSelect-select:focus': {
			background: 'unset',
		},
	},
	menuList: {
		'& > *': {
			fontSize: '14px',
		},
	},
}))

export const SelectYearlyMileage = ({
	asset,
	onChange,
	size = 'default',
}: {
	asset: Vehicle
	onChange: (yearlyMileage: YearlyMileageType) => void
	size?: 'small' | 'default'
}): JSX.Element | null => {
	const classes = useStyles()
	const menuProps = useMemo(
		() => ({
			MenuListProps: {
				className: classes.menuList,
			},
		}),
		[classes.menuList],
	)

	return (
		<Select
			className={size === 'small' ? classes.root : undefined}
			style={
				size === 'small'
					? { minWidth: '173px', height: '35px' }
					: { minWidth: '250px' }
			}
			value={asset.yearlyMileage ?? ''}
			values={Object.values(YearlyMileage).map((value) => ({
				value,
				displayValue: translateYearlyMileage(value),
			}))}
			onChange={(e) => onChange(e.target.value as YearlyMileageType)}
			variant="outlined"
			displayEmpty
			renderValue={(selected) =>
				selected ? (
					<SelectPlaceholderLabel isSelected={!!selected}>
						{translateYearlyMileage(selected as YearlyMileageType)}
					</SelectPlaceholderLabel>
				) : (
					<SelectPlaceholderLabel isSelected={!!selected}>
						{translateYearlyMileage(YearlyMileage.From5000To10000)}
					</SelectPlaceholderLabel>
				)
			}
			MenuProps={size === 'small' ? menuProps : undefined}
		/>
	)
}

const modelYearOf = (vehicle: AutocompletedVehicle): number => {
	if (vehicle.modelYear) return Number(vehicle.modelYear)
	if (vehicle.firstRegistrationDate) {
		const [year] = vehicle.firstRegistrationDate.split('-')
		return Number(year)
	}
	return 1950
}

const Search: React.FC<{
	assetType: AssetType
	overarchingType: OverarchingAssetType
	onSkipSearch: () => void
	onAdd: (asset: Asset) => void
}> = ({ assetType, overarchingType, onSkipSearch, onAdd }) => {
	const { t } = useTranslation()
	const skipText = {
		realEstate: t('asset-overview.add-asset.search-realEstate.skip'),
		vehicle: t('asset-overview.add-asset.search-vehicle.skip'),
	}[overarchingType]

	const header = {
		house: t('asset-overview.add-asset.search-house.header'),
		apartment: t('asset-overview.add-asset.search-apartment.header'),
		vacationHome: t('asset-overview.add-asset.search-vacationHome.header'),
		allotmentGardenHouse: t(
			'asset-overview.add-asset.search-allotmentGardenHouse.header',
		),
		farmhouse: t('asset-overview.add-asset.search-farmhouse.header'),
		buildingPlot: t('asset-overview.add-asset.search-buildingPlot.header'),
		houseboat: t('asset-overview.add-asset.search-houseboat.header'),
		car: t('asset-overview.add-asset.search-car.header'),
		motorcycle: t('asset-overview.add-asset.search-motorcycle.header'),
		rv: t('asset-overview.add-asset.search-rV.header'),
		campingWagon: t('asset-overview.add-asset.search-campingwagon.header'),
		boat: t('asset-overview.add-asset.search-boat.header'),
	}[assetType]

	return (
		<>
			<H3>{header}</H3>
			<Column>
				<Conditionally condition={overarchingType === 'realEstate'}>
					<AutoCompleteAddress
						onChange={(addressLookupResult) =>
							onAdd({
								assetType,
								address: addressLookupResult.address,
								bfeNumber: addressLookupResult.bfeNumber,
								valuation: addressLookupResult.realEstateValue,
							} as Asset)
						}
					/>
				</Conditionally>
				<Conditionally condition={overarchingType === 'vehicle'}>
					<AutoCompleteVehicle
						onChange={(vehicle) =>
							onAdd({
								assetType,
								brand: toCased(vehicle.brand),
								model: vehicle.model,
								variant: vehicle.variant,
								vehicleIdentificationNumber: vehicle.vin,
								year: modelYearOf(vehicle),
								propellant: vehicle.fuelType,
								registrationNumber: vehicle.registration,
								periodicTaxes: vehicle.periodicTaxes,
								valuation: vehicle.valuation,
							} as Asset)
						}
					/>
				</Conditionally>
				<TertiaryButton type="button" onClick={onSkipSearch}>
					{skipText}
				</TertiaryButton>
			</Column>
		</>
	)
}

type AddAssetStep =
	| 'selectType'
	| 'describe-search'
	| 'describe-manual'
	| 'financing-type'
	| 'cooperative-value'
	| 'yearly-mileage'

interface GoToNextStep {
	step: AddAssetStep
	asset: Partial<Asset>
}

const goToNextStep = (
	props: GoToNextStep,
	setAsset: Setter<Asset>,
	setCurrentStep: (step: AddAssetStep) => void,
	onAdd: (asset: Asset, files?: File[]) => void,
	overarchingType: OverarchingAssetType,
	shouldInputCooperativeHomeValue: boolean,
) => {
	switch (props.step) {
		case 'selectType': {
			setAsset({
				addedByApplicant: true,
				id: `added-by-user-${Date.now()}`,
				currency: 'DKK',
				assetType: props.asset.assetType,
			} as Asset)

			switch (true) {
				case overarchingTypes.realEstate.includes(
					props.asset.assetType as RealEstateUnion,
				) ||
					(overarchingTypes.vehicle.includes(
						props.asset.assetType as VehicleUnion,
					) &&
						props.asset.assetType !== AssetType.Boat): {
					setCurrentStep('describe-search')
					break
				}

				default: {
					setCurrentStep('describe-manual')
					break
				}
			}
			break
		}

		case 'describe-search':
		case 'describe-manual': {
			setAsset((asset) => ({ ...asset, ...props.asset }) as Asset)
			setCurrentStep('financing-type')
			break
		}

		case 'financing-type': {
			setAsset((asset) => ({ ...asset, ...props.asset }) as Asset)

			if (
				shouldInputCooperativeHomeValue &&
				(props.asset as RealEstate).financingType ===
					RealEstateFinancingType.Cooperative
			) {
				setCurrentStep('cooperative-value')
			} else if (
				overarchingType === 'vehicle' &&
				props.asset.assetType !== AssetType.CampingWagon &&
				(props.asset as Vehicle).yearlyMileage === undefined
			) {
				setCurrentStep('yearly-mileage')
			} else {
				const assetToAdd =
					props.asset.assetType === AssetType.CampingWagon
						? { ...(props.asset as Vehicle), yearlyMileage: null }
						: (props.asset as Asset)
				onAdd(assetToAdd as Asset, [])
			}
			break
		}
		case 'cooperative-value': {
			setAsset((asset) => ({ ...asset, ...props.asset }) as Asset)
			onAdd(props.asset as Asset, [])
			break
		}
		case 'yearly-mileage': {
			setAsset((asset) => ({ ...asset, ...props.asset }) as Asset)
			onAdd(props.asset as Asset, [])
			break
		}
	}
}

const useAddAssetTitle = (
	step: AddAssetStep,
	overarchingType: OverarchingAssetType,
): string => {
	const { t } = useTranslation()

	switch (step) {
		case 'selectType':
			return t('asset-overview.add-asset.select-type.title')
		case 'describe-search':
			switch (overarchingType) {
				case 'realEstate':
					return t('asset-overview.add-asset.search-realEstate.title')
				case 'vehicle':
					return t('asset-overview.add-asset.search-vehicle.title')
			}
		case 'describe-manual':
			switch (overarchingType) {
				case 'realEstate':
					return t('asset-overview.add-asset.enter-manual-realEstate.title')
				case 'vehicle':
					return t('asset-overview.add-asset.enter-manual-vehicle.title')
			}
		case 'financing-type':
			switch (overarchingType) {
				case 'realEstate':
					return t('asset-overview.add-asset.financingType-realEstate.title')
				case 'vehicle':
					return t('asset-overview.add-asset.financingType-vehicle.title')
			}
		case 'cooperative-value':
			return t('asset-overview.add-asset.cooperative-value.title')
		case 'yearly-mileage':
			return t('asset-overview.add-asset.yearly-mileage.title')
	}
}

const stepsWithNextButton = ['valuation', 'describe-manual', 'documentation', 'cooperative-value']

export const AddAsset: React.FC<{
	onAdd: (asset: Asset, files?: File[]) => void
	onCancel: () => void
	overarchingType: OverarchingAssetType
	shouldInputCooperativeHomeValue?: boolean
}> = ({ onAdd, onCancel, overarchingType, shouldInputCooperativeHomeValue = false }) => {
	const [asset, setAsset] = useState<Partial<Asset>>({})

	const { t } = useTranslation()
	const intlConfig = useIntlConfig()

	const { currentStep, setCurrentStep, goBack } =
		useSteps<AddAssetStep>('selectType')

	const onNext = useCallback(
		(props: GoToNextStep) =>
			goToNextStep(
				props,
				setAsset,
				(step) => setCurrentStep(step),
				onAdd,
				overarchingType,
				shouldInputCooperativeHomeValue,
			),
		[onAdd, overarchingType, setCurrentStep, shouldInputCooperativeHomeValue],
	)

	const title = useAddAssetTitle(currentStep, overarchingType)

	const formErrors = validateSpecificFields(asset)

	const buttons = [
		<SecondaryButton type="button" key="cancel-button" onClick={onCancel}>
			{t('btn-cancel')}
		</SecondaryButton>,
	]

	const goToNext = () => onNext({ asset: asset as Asset, step: currentStep })
	const cooperativeValueMissing =
		currentStep === 'cooperative-value' &&
		(asset as RealEstate).assetValueAddedByApplicant === undefined
	const goToNextDisabled =
		cooperativeValueMissing || (formErrors && Object.keys(formErrors).length > 0)

	if (stepsWithNextButton.includes(currentStep))
		buttons.push(
			<PrimaryButton
				onClick={goToNext}
				key="primary"
				type="button"
				disabled={goToNextDisabled}
			>
				{t('btn-next')}
			</PrimaryButton>,
		)

	const steps = useMemo(
		() => ({
			selectType: (
				<SelectAssetSubType
					key={'selectType'}
					overarchingType={overarchingType}
					onChange={(subType) =>
						onNext({ asset: { assetType: subType }, step: 'selectType' })
					}
				/>
			),
			'describe-search': (
				<Background key={'describe-search'}>
					<Search
						assetType={asset.assetType!}
						overarchingType={overarchingType}
						onSkipSearch={() => setCurrentStep('describe-manual')}
						onAdd={(fields) =>
							onNext({ step: 'describe-search', asset: fields })
						}
					/>
				</Background>
			),
			'describe-manual': (
				<Background key={'describe-manual'}>
					<AssetTypeSpecific
						asset={asset as Asset}
						setAsset={setAsset}
						formErrors={formErrors}
					/>
				</Background>
			),
			'financing-type': (
				<Background key={'financing-type'}>
					<SelectFinancingType
						onChange={(financingType) =>
							onNext({
								asset: { ...asset, financingType } as Asset,
								step: 'financing-type',
							})
						}
						asset={asset}
					/>
				</Background>
			),
			...(overarchingType === 'vehicle'
				? {
						'yearly-mileage': (
							<Background key={'yearly-mileage'}>
								<H3>
									{t('asset-overview.add-asset.select-yearly-mileage.header')}
								</H3>
								<SelectYearlyMileage
									onChange={(yearlyMileage) =>
										onNext({
											asset: { ...asset, yearlyMileage } as Vehicle,
											step: 'yearly-mileage',
										})
									}
									asset={asset as Vehicle}
								/>
							</Background>
						),
					}
				: {}),
			...(shouldInputCooperativeHomeValue
				? {
						'cooperative-value': (
							<Background key={'cooperative-value'}>
								<div style={{display: 'flex', justifyContent: 'space-between'}}>
									<H3>{t('asset-overview.cooperative.value-label')}</H3>
									<BoxInput
										value={(asset as RealEstate).assetValueAddedByApplicant ?? ''}
										error={(asset as RealEstate).assetValueAddedByApplicant === undefined}
										onChange={(e) => {
											const raw = parseFloat(e.target.value)
											setAsset((a) => ({
												...a,
												assetValueAddedByApplicant: isNaN(raw) ? undefined : raw,
											}))
										}}
										currencyInputProps={{
											allowDecimals: false,
											allowNegativeValue: false,
											intlConfig,
											maxLength: 10,
										}}
									/>
								</div>
							</Background>
						),
					}
				: {}),
		}),
		[
			asset,
			formErrors,
			onNext,
			overarchingType,
			setCurrentStep,
			shouldInputCooperativeHomeValue,
			t,
		],
	)

	return (
		<FlowPage>
			<FlowPage.Content $spacing="m">
				<FlowPage.Header title={title} />
				{steps[currentStep]}
			</FlowPage.Content>
			<FlowPage.Footer
				backButton={
					goBack !== undefined ? (
						<FlowPage.BackButton onClick={goBack} ariaLabel={t('btn-back')} />
					) : undefined
				}
				actions={[
					<FlowPage.SecondaryAction key="cancel" onClick={onCancel}>
						{t('btn-cancel')}
					</FlowPage.SecondaryAction>,
					stepsWithNextButton.includes(currentStep) ? (
						<FlowPage.Cta
							key="next"
							onClick={goToNext}
							disabled={goToNextDisabled}
						>
							{t('btn-next')}
						</FlowPage.Cta>
					) : null,
				]}
			/>
		</FlowPage>
	)
}
